'use client'
import { useState, useEffect } from 'react'
import { createClient } from '@/lib/supabase/client'
import { toast } from 'sonner'

export default function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [isAldar, setIsAldar] = useState(false)
  const [schoolSlug, setSchoolSlug] = useState('')
  const [branding, setBranding] = useState({
    name: 'Fast & Curious EduPortal',
    tagline: 'Never Lose Your Sense Of Wonder, Stay Fast, Stay Curious!',
    primary: '#2BBCB8',
    secondary: '#1a9e9b',
  })

  const supabase = createClient()

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const network = params.get('network')
    const school = params.get('school')

    if (network === 'aldar') {
      setIsAldar(true)
      setBranding({
        name: 'ALDAR Education',
        tagline: 'Empowering the Next Generation to Shape Our Future',
        primary: '#1B2A6B',
        secondary: '#C9A84C',
      })
    } else if (school) {
      setSchoolSlug(school)
      if (school.startsWith('aldar-')) setIsAldar(true)

      supabase
        .from('schools')
        .select('name, tagline, primary_color, secondary_color, slug')
        .eq('slug', school)
        .single()
        .then(({ data }) => {
          if (data) {
            setBranding(prev => ({
              name: data.name,
              tagline: data.tagline ?? prev.tagline,
              primary: data.primary_color ?? '#2BBCB8',
              secondary: data.secondary_color ?? '#1a9e9b',
            }))
          }
        })
    }
  }, [supabase])

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)

    try {
      const { data: authData, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) {
        toast.error(error.message)
        return
      }

      const userId = authData.user?.id
      if (!userId) {
        toast.error('Login failed.')
        return
      }

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('role, school_id, network_id')
        .eq('user_id', userId)
        .single()

      if (profileError || !profile) {
        toast.error('Profile not found. Contact your administrator.')
        return
      }

      toast.success('Welcome back!')

      const r = profile.role
      let dest = '/login'

      if (r === 'super_admin') {
        dest = '/superadmin/schools'
      } else if (r === 'management' && profile.network_id) {
        dest = '/network-admin/dashboard'
      } else if (r === 'management' && profile.school_id) {
        dest = '/school-admin/dashboard'
      } else if (r === 'principal') {
        dest = '/school-admin/dashboard'
      } else if (r === 'teacher') {
        dest = '/teacher/dashboard'
      } else if (r === 'student') {
        dest = '/student/dashboard'
      } else {
        toast.error('Your account role is not configured correctly.')
        return
      }

      window.location.href = dest
    } catch (err) {
      console.error('Login error:', err)
      toast.error('Something went wrong.')
    } finally {
      setLoading(false)
    }
  }

  const panelBg = isAldar
    ? 'linear-gradient(160deg, #050a1a 0%, #1B2A6B 100%)'
    : `linear-gradient(160deg, ${branding.primary}44 0%, ${branding.secondary} 100%)`

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-3xl bg-white rounded-3xl shadow-md overflow-hidden flex">

        <div
          className="w-80 flex-shrink-0 p-10 flex flex-col justify-between"
          style={{ background: panelBg }}
        >
          <div>
            {isAldar ? (
              <img
                src="/aldar-logo.png"
                alt="ALDAR Education"
                className="h-28 w-auto mb-6 object-contain"
              />
            ) : (
              <img
                src="/logo.png"
                alt="Fast & Curious"
                className="h-20 w-auto mb-6 object-contain"
                style={{ filter: 'h-20 w-auto mb-6 object-contain' }}
              />
            )}

            <p className="text-white/70 text-xs font-bold uppercase tracking-widest mb-6">
              {branding.name}
            </p>

            <img
              src="/aldar-logo-transparent.webp"
              alt="Aldar Education"
              className="mx-auto w-32 opacity-90"
            />
			<br/>

            {isAldar && (
              <p className="text-white/50 text-xs mt-1">
                Powered by Fast & Curious EduPortal
              </p>
            )}
          </div>

          <p className="text-white font-bold text-sm leading-relaxed">
            {branding.tagline}
          </p>
        </div>

        <div className="flex-1 p-10">
          <h1 className="font-playfair text-2xl font-bold text-gray-800 mb-1">
            Welcome back 👋
          </h1>
          <p className="text-sm text-gray-500 mb-7">Sign in to your portal</p>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="form-label">Email Address</label>
              <input
                className="form-input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@school.edu"
                required
              />
            </div>

            <div>
              <label className="form-label">Password</label>
              <input
                className="form-input"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-full text-white font-bold text-base disabled:opacity-60 transition-all"
              style={{
                background: `linear-gradient(135deg, ${branding.primary}, ${branding.secondary})`,
              }}
            >
              {loading ? 'Signing in…' : 'Sign In →'}
            </button>
          </form>

          <p className="mt-5 text-xs text-gray-400 text-center">
            Need an account? Contact your school administrator.
          </p>
        </div>
      </div>
    </div>
  )
}